import { Button, Skeleton } from '@bpinternal/ui-kit-next'
import { Link } from '@tanstack/react-router'
import { Check } from 'lucide-react'
import { usePlanSummary } from '~/features/billing/api'
import { trackEvent } from '~/providers'
import { formatters } from '~/utils'
import { Boundary } from './Boundary'

type HelpDeskUpgradeDialogProps = {
  close: () => void
  workspaceId: string
}

export const HelpDeskUpgradeDialog = ({ close, workspaceId }: HelpDeskUpgradeDialogProps) => {
  return (
    <div className="flex flex-col gap-6">
      <p className="text-sm text-muted-foreground">
        The Help Desk is a premium feature available on paid plans. When you upgrade, you'll get access to:
      </p>
      <ul className="flex flex-col gap-4 rounded-xl border bg-card p-4 text-card-foreground shadow-xs">
        <li className="flex flex-row items-center gap-2">
          <Check size={16} color="green" />
          <p>Priority support from Botpress experts</p>
        </li>
        <li className="flex flex-row items-center gap-2">
          <Check size={16} color="green" />
          <p>Fast, human-to-human support</p>
        </li>
        <li className="flex flex-row items-center gap-2">
          <Check size={16} color="green" />
          <p>Priority bugfix escalation</p>
        </li>
        <li className="flex flex-row items-center gap-2">
          <Check size={16} color="green" />
          <p>Guidance from our team of Support Engineers</p>
        </li>
      </ul>

      <p className="text-sm text-muted-foreground">
        Need help now? Check out our{' '}
        <a
          href="https://botpress.com/docs/home"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary underline-offset-4 hover:underline"
        >
          documentation
        </a>
        ,{' '}
        <a
          href="https://discord.com/invite/botpress"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary underline-offset-4 hover:underline"
        >
          Discord
        </a>
        , or{' '}
        <a
          href="https://www.youtube.com/botpress"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary underline-offset-4 hover:underline"
        >
          Youtube
        </a>{' '}
        channel.
      </p>
      <Boundary suspenseFallback={<Skeleton className="h-9 w-full max-w-80 self-center rounded" />}>
        <ExpertSupportCta workspaceId={workspaceId} close={close} />
      </Boundary>
    </div>
  )
}

function ExpertSupportCta({ workspaceId, close }: { workspaceId: string; close: () => void }) {
  const { planSummary } = usePlanSummary(workspaceId, 'plus')
  const priceLabel =
    planSummary.monthlyPriceDollars != null
      ? ` starting at ${formatters.$dollars(planSummary.monthlyPriceDollars)}/month`
      : ''
  return (
    <Link
      to="/workspaces/$workspaceId/billing"
      className="self-center"
      params={{ workspaceId }}
      onClick={() => {
        trackEvent({ type: 'get_expert_support', workspaceId })
        close()
      }}
    >
      <Button>Get expert support{priceLabel}</Button>
    </Link>
  )
}
